import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from 'lucide-react';
import { pagePreviews } from '../data/pagePreviews';
export function PagePreviews() {
  const trackRef = useRef<HTMLUListElement>(null);
  const [active, setActive] = useState(0);
  const scrollToIndex = useCallback((index: number) => {
    const track = trackRef.current;
    if (!track) return;
    const item = track.children[index] as HTMLElement | undefined;
    if (!item) return;
    track.scrollTo({
      left: item.offsetLeft - track.offsetLeft,
      behavior: 'smooth'
    });
  }, []);
  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    let frame = 0;
    const onScroll = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const center = track.scrollLeft + track.clientWidth / 2;
        let closest = 0;
        let smallest = Infinity;
        Array.from(track.children).forEach((child, i) => {
          const el = child as HTMLElement;
          const itemCenter = el.offsetLeft - track.offsetLeft + el.offsetWidth / 2;
          const distance = Math.abs(itemCenter - center);
          if (distance < smallest) {
            smallest = distance;
            closest = i;
          }
        });
        setActive(closest);
      });
    };
    track.addEventListener('scroll', onScroll, {
      passive: true
    });
    return () => {
      cancelAnimationFrame(frame);
      track.removeEventListener('scroll', onScroll);
    };
  }, []);
  const atStart = active === 0;
  const atEnd = active === pagePreviews.length - 1;
  return <section aria-label="Sample pages from inside the ebook" className="mt-12" data-id="element-116" data-mp="n457f5l3om6">
      <div className="text-center" data-id="element-117" data-mp="5l5lxtfjfbuh">
        <p className="font-display text-lg font-black uppercase tracking-[0.18em] text-plum-soft" data-id="element-118" data-mp="5k4avoyiupcr">
          👀 A Look Inside The Pages
        </p>
        <p className="mx-auto mt-2 max-w-xl text-sm text-plum/60" data-id="element-119" data-mp="27bqw19vqfq1b">
          Real spreads from the system — printable trackers, step-by-step movements, safety maps and
          emergency protocols.
        </p>
      </div>

      <div className="relative mt-8" data-id="element-120" data-mp="1udrr3xyyzanx">
        <ul ref={trackRef} className="flex snap-x snap-mandatory gap-4 overflow-x-auto scroll-smooth pb-4 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden" data-id="element-121" data-mp="1ueugu2kk3729">
          {pagePreviews.map((p, i) => <li key={p.id} className="w-[78%] shrink-0 snap-center sm:w-[46%] lg:w-[31%]" aria-label={`${i + 1} of ${pagePreviews.length}`} data-id="element-122" data-mp="2i947vz2dpvcq" data-mpi={i}>
              <figure className="flex h-full flex-col overflow-hidden rounded-2xl border border-grape/20 bg-white" data-id="element-123" data-mp="2rz71a0dexbbl">
                <img src={p.src} alt={p.title} width={1080} height={1350} loading={i === 0 ? 'eager' : 'lazy'} decoding="async" sizes="(min-width: 1024px) 31vw, (min-width: 640px) 46vw, 78vw" className="aspect-[4/5] w-full bg-white object-contain" data-id="element-124" data-mp="2n1nkaspbykfy" />
                <figcaption className="flex items-center gap-2 border-t border-grape/10 bg-cream px-4 py-3" data-id="element-125" data-mp="3klx6pw2sdm4b">
                  <span className="shrink-0 rounded-full bg-lilac-soft px-2.5 py-1 text-[10px] font-bold uppercase tracking-wide text-grape" data-id="element-126" data-mp="36p1ope4k4kc4">
                    {p.label}
                  </span>
                  <span className="truncate text-xs font-semibold text-plum/70" data-id="element-127" data-mp="2wv19gjfk9ypo">{p.title}</span>
                </figcaption>
              </figure>
            </li>)}
        </ul>

        <div className="mt-2 flex items-center justify-center gap-4" data-id="element-128" data-mp="2nyaeybgnqrx7">
          <button type="button" onClick={() => scrollToIndex(Math.max(0, active - 1))} disabled={atStart} aria-label="Previous page preview" className="flex h-10 w-10 items-center justify-center rounded-full border border-grape/25 bg-white text-grape transition-[background-color,opacity] duration-150 ease-out hover:bg-lilac-soft disabled:opacity-35 focus:outline-none focus-visible:ring-2 focus-visible:ring-grape" data-id="element-129" data-mp="2rz2pr956h4vl">
            <ChevronLeftIcon className="h-5 w-5" data-id="element-130" data-mp="3chnq3x69ux5l" />
          </button>

          <div className="flex items-center gap-2" data-id="element-131" data-mp="37jdxnubxmano">
            {pagePreviews.map((p, i) => <button key={p.id} type="button" onClick={() => scrollToIndex(i)} aria-label={`Go to page preview ${i + 1}`} aria-current={i === active} className={`h-2 rounded-full transition-[width,background-color] duration-200 ease-out ${i === active ? 'w-6 bg-grape' : 'w-2 bg-grape/25 hover:bg-grape/50'}`} data-id="element-132" data-mp="32k9nb0d9u6vr" data-mpi={i} />)}
          </div>

          <button type="button" onClick={() => scrollToIndex(Math.min(pagePreviews.length - 1, active + 1))} disabled={atEnd} aria-label="Next page preview" className="flex h-10 w-10 items-center justify-center rounded-full border border-grape/25 bg-white text-grape transition-[background-color,opacity] duration-150 ease-out hover:bg-lilac-soft disabled:opacity-35 focus:outline-none focus-visible:ring-2 focus-visible:ring-grape" data-id="element-133" data-mp="rpvod8zppyce">
            <ChevronRightIcon className="h-5 w-5" data-id="element-134" data-mp="bds6u8b3hvyx" />
          </button>
        </div>
      </div>
    </section>;
}